export type PlanPoint = [number, number];

export function squareEndpoint(start: PlanPoint, point: PlanPoint): PlanPoint {
  const dx = point[0] - start[0], dy = point[1] - start[1];
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  return [start[0] + Math.sign(dx || 1) * side, start[1] + Math.sign(dy || 1) * side];
}

export function regularPolygonPoints(center: PlanPoint, edge: PlanPoint, sides = 6): PlanPoint[] {
  const radius = Math.hypot(edge[0] - center[0], edge[1] - center[1]);
  if (radius < .001 || sides < 3) return [];
  const angle = Math.atan2(edge[1] - center[1], edge[0] - center[0]);
  return Array.from({ length: sides }, (_, index) => [center[0] + radius * Math.cos(angle + index * 2 * Math.PI / sides), center[1] + radius * Math.sin(angle + index * 2 * Math.PI / sides)]);
}

/** Circular arc through start, bulge and end, in that order. */
export function arcThroughPoints(start: PlanPoint, end: PlanPoint, bulge: PlanPoint, segments = 32): PlanPoint[] {
  const [ax, ay] = start, [bx, by] = bulge, [cx, cy] = end;
  const determinant = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(determinant) < .001) return [start, bulge, end];
  const a = ax * ax + ay * ay, b = bx * bx + by * by, c = cx * cx + cy * cy;
  const centerX = (a * (by - cy) + b * (cy - ay) + c * (ay - by)) / determinant;
  const centerY = (a * (cx - bx) + b * (ax - cx) + c * (bx - ax)) / determinant;
  const radius = Math.hypot(ax - centerX, ay - centerY);
  const startAngle = Math.atan2(ay - centerY, ax - centerX);
  const endAngle = Math.atan2(cy - centerY, cx - centerX);
  const bulgeAngle = Math.atan2(by - centerY, bx - centerX);
  const positive = (angle: number) => (angle + 2 * Math.PI) % (2 * Math.PI);
  const ccw = positive(endAngle - startAngle);
  const sweep = positive(bulgeAngle - startAngle) <= ccw ? ccw : ccw - 2 * Math.PI;
  return Array.from({ length: segments + 1 }, (_, index) => [centerX + radius * Math.cos(startAngle + sweep * index / segments), centerY + radius * Math.sin(startAngle + sweep * index / segments)]);
}
